// QUÉ SE HA COBRADO. Un sitio, una respuesta.
//
// ⚠️ ESTE FICHERO SALE DE UNA MEDICIÓN QUE DUELE. El dashboard calculaba los
// ingresos desde `user_credits × session_packs.price_cents`, y `user_credits`
// tiene CUATRO filas en toda la base de producción. Medido el 4-oct-2026:
//
//     septiembre   dashboard   160 €   ·   cobrado  2.275 €  (22 pedidos)
//     octubre      dashboard     0 €   ·   cobrado    360 €  ( 6 pedidos)
//
// O sea que el KPI «Ingresos este mes» decía CERO con 360 € cobrados dentro del
// mes. No era un redondeo: el dinero entra por `orders` y la pantalla lo
// buscaba en una tabla que casi no se usa. Histórico: 29 pedidos, 2.636 €.
//
// Y la respuesta correcta YA ESTABA en el panel: `/estadisticas` filtra
// `status IN ('paid','shipped','delivered')` desde hace migraciones. Una
// pantalla decía la verdad y la otra no, así que la lista se escribe aquí una
// vez y la leen las dos.
//
// ── POR QUÉ ESOS TRES ESTADOS, Y POR QUÉ ES UNA LISTA BLANCA ─────────────
// La máquina es `pending → paid → shipped → delivered`, más `cancelled` y
// `expired` (CHECK `orders_status_check`). Cobrado es a partir de `paid`:
// `shipped` y `delivered` son pedidos que ya se pagaron y además salieron.
//
// Medido hoy en producción, y por eso la lista no es solo `'paid'`:
//   · `paid`      17 pedidos · 2.275 €
//   · `delivered` 12 pedidos ·   361 €   ← se quedaban fuera
//   · `shipped`    0 pedidos
//   · `expired`   17 pedidos · 1.241 €   (carrito abandonado, nunca fue venta)
//   · `cancelled`  3 pedidos ·   271 €
//
// Los 361 € de `delivered` son, además, los que explican el mes en curso: 360
// de esos euros se cobraron en octubre. Contar solo `'paid'` habría dejado el
// KPI de este mes en cero igual que antes, con otro motivo.
//
// ⚠️ Y NO SE CAMBIA POR «los que tienen payment intent». Lo avisa el comentario
// de `/estadisticas` y lo confirman los datos: los canjes gratuitos de
// `redeem_free_cart` nacen `delivered`, a 0 € y SIN intent. Ese filtro borraría
// ventas reales — hoy ocho pedidos de regalo.
//
// `total_cents` YA ES NETO: comprobado sobre los 8 pedidos con descuento,
// `total_cents` = Σ `items[].final_cents`, y `discount_total_cents` va aparte.
// Restarlo otra vez contaría el descuento dos veces.

/**
 * Los estados en los que el dinero ha entrado.
 *
 * Lista BLANCA a propósito: un estado nuevo en la base no se cuenta como
 * ingreso hasta que alguien lo ponga aquí. Al revés —una lista negra— un
 * estado nuevo entraría solo, y el primer sitio donde se notaría sería la
 * cifra de ingresos.
 */
export const ESTADOS_COBRADOS = ['paid', 'shipped', 'delivered'] as const;

/** Lo mínimo que hace falta de un pedido para sumarlo. */
export interface PedidoCobrado {
  status: string;
  total_cents: number | null;
  created_at: string;
}

export function estaCobrado(status: string): boolean {
  return (ESTADOS_COBRADOS as readonly string[]).includes(status);
}

/** Los euros cobrados de una lista de pedidos. Ignora lo que no esté cobrado. */
export function eurosCobrados(pedidos: readonly PedidoCobrado[]): number {
  return pedidos
    .filter((p) => estaCobrado(p.status))
    .reduce((suma, p) => suma + (p.total_cents ?? 0), 0) / 100;
}

/**
 * Los euros cobrados en un intervalo `[desde, hasta)`.
 *
 * El intervalo es medio abierto para que dos meses consecutivos no se solapen
 * en el último milisegundo. El de antes usaba `gte`/`lte` sobre el fin de mes,
 * así que un pedido sellado exactamente a las 23:59:59.999 del día 30 contaba
 * en los dos.
 */
export function eurosCobradosEntre(
  pedidos: readonly PedidoCobrado[],
  desde: Date,
  hasta: Date
): number {
  const d = desde.getTime();
  const h = hasta.getTime();
  return eurosCobrados(
    pedidos.filter((p) => {
      const t = new Date(p.created_at).getTime();
      return t >= d && t < h;
    })
  );
}
